// Runs Revert VM to Snapshot in the background. See revertRuns.spec.md (RVR-FR-003..005)
// and useRevertVmRunner.spec.md (URVR-FR-001 to URVR-FR-006)
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { revertVmRollbackVolumes } from 'openstack/components/storage/volumes/actions'
import { getVirtualMachine, rebuildVirtualMachine } from 'openstack/components/vms/actions'
import {
  getLatestRebuildResult,
  getRevertRequestIds,
  getRevertStatus,
  hasTimedOut,
  isRebuilding,
  isRunActive,
  POLL_INTERVAL_MS,
  REBUILD_TIMEOUT_MS,
  type RevertRun,
  type RevertRunVolume,
  sleep,
} from './revertRuns'

export interface StartRevertRunParams {
  vm: { id: string; name?: string }
  snapshot: { id: string; name?: string }
  // Data volumes to revert, each with the volume snapshot of the VM snapshot
  volumes: Array<{ id: string; name: string; snapshotId: string }>
}

export interface RevertVmRunner {
  runs: RevertRun[]
  startRun: (params: StartRevertRunParams) => string
  dismissRuns: (ids: string[]) => void
}

export default function useRevertVmRunner(): RevertVmRunner {
  const [runs, setRuns] = useState<RevertRun[]>([])
  const runsRef = useRef<RevertRun[]>([])
  runsRef.current = runs
  const { update: rebuildVm } = useUpdateAction(rebuildVirtualMachine)
  const { update: revertVolumes } = useUpdateAction(revertVmRollbackVolumes)

  // URVR-FR-005
  // Polling only lives as long as the app: it stops when the provider unmounts
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const patchRun = useCallback((id: string, patch: (run: RevertRun) => Partial<RevertRun>) => {
    setRuns((previous) => previous.map((run) => (run.id === id ? { ...run, ...patch(run) } : run)))
  }, [])

  const patchVolumes = useCallback(
    (id: string, statuses: Record<string, RevertRunVolume['status']>) =>
      patchRun(id, (run) => ({
        volumes: run.volumes.map((volume) => ({
          ...volume,
          status: statuses[volume.id] ?? volume.status,
        })),
      })),
    [patchRun],
  )

  // URVR-FR-002
  // RVR-FR-003: rebuild the VM, then wait until it is out of the rebuilding task state and the
  // rebuild event says success. Returns whether the rebuild worked.
  const rebuild = useCallback(
    async (runId: string, { vm, snapshot }: StartRevertRunParams) => {
      patchRun(runId, () => ({ rebuildStatus: 'inProgress' }))
      const { success } = await rebuildVm({
        id: vm.id,
        body: { rebuild: { imageRef: snapshot.id } },
      })
      const fail = (error: string) => {
        patchRun(runId, () => ({ rebuildStatus: 'failed', error }))
        return false
      }
      if (!success) return fail('The VM rebuild could not be started.')
      const waitStartedAt = Date.now()
      while (true) {
        await sleep(POLL_INTERVAL_MS)
        if (!mountedRef.current) return false
        // RVR-FR-008
        if (hasTimedOut(waitStartedAt, REBUILD_TIMEOUT_MS)) {
          return fail('The VM rebuild did not finish within 15 minutes.')
        }
        try {
          if (isRebuilding(await getVirtualMachine({ id: vm.id }))) continue
          const result = await getLatestRebuildResult(vm.id)
          if (result === 'success') break
          if (result === 'failed') return fail('The VM rebuild did not complete successfully.')
        } catch {
          // Transient API error: keep polling
        }
      }
      patchRun(runId, () => ({ rebuildStatus: 'completed' }))
      return true
    },
    [patchRun, rebuildVm],
  )

  // URVR-FR-003
  // RVR-FR-004: revert every data volume, then follow each volume's latest revert event
  const revertDataVolumes = useCallback(
    async (runId: string, { volumes }: StartRevertRunParams) => {
      // Revert events recorded before this run are remembered so only its own are tracked
      const previousRequestIds: Record<string, Set<string>> = {}
      await Promise.all(
        volumes.map(async ({ id }) => {
          previousRequestIds[id] = await getRevertRequestIds(id).catch(() => new Set<string>())
        }),
      )
      patchVolumes(runId, Object.fromEntries(volumes.map(({ id }) => [id, 'inProgress'])))
      const { success, response } = await revertVolumes({
        volumes: volumes.map(({ id, snapshotId }) => ({ id, snapshotId })),
      })
      const rejected = new Set(
        success
          ? (response ?? [])
              .filter((result) => result.status === 'rejected')
              .map((result) => result.id)
          : volumes.map(({ id }) => id),
      )
      patchVolumes(runId, Object.fromEntries([...rejected].map((id) => [id, 'failed'])))

      // No timeout here, unlike the rebuild: this is the last step and nothing waits on it
      let pending = volumes.map(({ id }) => id).filter((id) => !rejected.has(id))
      while (pending.length) {
        await sleep(POLL_INTERVAL_MS)
        if (!mountedRef.current) return
        const updates = await Promise.all(
          pending.map(async (id) => {
            try {
              return [id, await getRevertStatus(id, previousRequestIds[id])] as const
            } catch {
              return [id, 'inProgress'] as const
            }
          }),
        )
        patchVolumes(runId, Object.fromEntries(updates))
        pending = updates.filter(([, status]) => status === 'inProgress').map(([id]) => id)
      }
    },
    [patchVolumes, revertVolumes],
  )

  // URVR-FR-001, URVR-FR-004
  // RVR-FR-005: returns the id of the run; a VM with a run in progress gets that run back
  const startRun = useCallback(
    (params: StartRevertRunParams): string => {
      const active = runsRef.current.find((run) => run.vmId === params.vm.id && isRunActive(run))
      if (active) return active.id

      const run: RevertRun = {
        id: `${params.vm.id}-${Date.now()}`,
        vmId: params.vm.id,
        vmName: params.vm.name || params.vm.id,
        snapshotName: params.snapshot.name || params.snapshot.id,
        rebuildStatus: 'waiting',
        volumes: params.volumes.map((volume) => ({ ...volume, status: 'waiting' })),
        error: null,
        startedAt: Date.now(),
      }
      // Newest first. The ref is updated right away so a second call before the next render
      // still finds this run.
      runsRef.current = [run, ...runsRef.current]
      setRuns((previous) => [run, ...previous])
      void (async () => {
        if ((await rebuild(run.id, params)) && params.volumes.length) {
          await revertDataVolumes(run.id, params)
        }
      })()
      return run.id
    },
    [rebuild, revertDataVolumes],
  )

  // URVR-FR-006: removes the given runs that have ended; active runs stay
  const dismissRuns = useCallback((ids: string[]) => {
    setRuns((previous) => previous.filter((run) => !ids.includes(run.id) || isRunActive(run)))
  }, [])

  return useMemo(() => ({ runs, startRun, dismissRuns }), [runs, startRun, dismissRuns])
}
